'use client';

import { FC, memo } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { coldarkDark } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import { Button } from '@/components/ui/button';
import { IconCheck, IconCopy, IconDownload } from './icons';
import { useCopyToClipboard } from './hooks/use-copy-to-clipboard';

interface Props {
	language: string;
	value: string;
}

interface languageMap {
	[key: string]: string | undefined;
}

export const programmingLanguages: languageMap = {
	javascript: '.js',
	python: '.py',
	java: '.java',
	c: '.c',
	cpp: '.cpp',
	'c++': '.cpp',
	'c#': '.cs',
	ruby: '.rb',
	php: '.php',
	swift: '.swift',
	'objective-c': '.m',
	kotlin: '.kt',
	typescript: '.ts',
	go: '.go',
	perl: '.pl',
	rust: '.rs',
	scala: '.scala',
	haskell: '.hs',
	lua: '.lua',
	shell: '.sh',
	sql: '.sql',
	html: '.html',
	css: '.css'
};

export const generateRandomString = (length: number, lowercase = false) => {
	const chars = 'ABCDEFGHJKLMNPQRSTUVWXY3456789';
	let result = '';
	for (let i = 0; i < length; i++) {
		result += chars.charAt(Math.floor(Math.random() * chars.length));
	}
	return lowercase ? result.toLowerCase() : result;
};

const CodeBlock: FC<Props> = memo(({ language, value }) => {
	const { isCopied, copyToClipboard } = useCopyToClipboard({ timeout: 2000 });

	const downloadAsFile = () => {
		if (typeof window === 'undefined') {
			return;
		}
		const fileExtension = programmingLanguages[language] || '.file';
		const suggestedFileName = `file-${generateRandomString(3, true)}${fileExtension}`;
		const fileName = window.prompt('Enter file name', suggestedFileName);

		if (!fileName) {
			return;
		}

		const blob = new Blob([value], { type: 'text/plain' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.download = fileName;
		link.href = url;
		link.style.display = 'none';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	};

	const onCopy = () => {
		if (isCopied) return;
		copyToClipboard(value);
	};

	return (
		<div className="lb-not-prose lb-relative lb-w-full lb-rounded-xl lb-bg-zinc-950 lb-font-sans">
			<div className="lb-flex lb-w-full lb-items-center lb-justify-between lb-rounded-xl lb-bg-zinc-800 lb-px-6 lb-py-2 lb-pr-4 lb-text-zinc-100">
				<span className="lb-text-xs lb-lowercase">{language}</span>
				<div className="lb-flex lb-items-center lb-space-x-1">
					<Button
						variant="ghost"
						className="hover:lb-bg-zinc-800 focus-visible:lb-ring-1 focus-visible:lb-ring-slate-700 focus-visible:lb-ring-offset-0"
						onClick={downloadAsFile}
						size="icon"
					>
						<IconDownload />
						<span className="lb-sr-only">Download</span>
					</Button>
					<Button
						variant="ghost"
						size="icon"
						className="lb-text-xs hover:lb-bg-zinc-800 focus-visible:lb-ring-1 focus-visible:lb-ring-slate-700 focus-visible:lb-ring-offset-0"
						onClick={onCopy}
					>
						{isCopied ? <IconCheck /> : <IconCopy />}
						<span className="lb-sr-only">Copy code</span>
					</Button>
				</div>
			</div>
			<SyntaxHighlighter
				language={language}
				style={coldarkDark}
				PreTag="div"
				showLineNumbers
				customStyle={{
					margin: 0,
					width: '100%',
					background: 'transparent',
					padding: '1.5rem 1rem'
				}}
				codeTagProps={{
					style: {
						fontSize: '0.9rem',
						fontFamily: 'var(--lb-font-mono)'
					}
				}}
			>
				{value}
			</SyntaxHighlighter>
		</div>
	);
});

CodeBlock.displayName = 'CodeBlock';

export { CodeBlock };
